import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import {
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  MessageCircle,
  Wallet,
  Activity,
  UserPlus,
  Check,
  X,
  Save,
  Download,
} from "lucide-react";
import { buildReminderMessage, buildCoachNotifyMessage, buildFeeReminderMessage, openWhatsApp } from "@/lib/whatsapp";
import { toast } from "sonner";
import { fmtDate, fmtMonthPeriod, currentMonthKey, fmtDateDMY, todayKey } from "@/lib/utils_dates";

// Every payment for a student: the itemised `transactions` plus any earlier
// money that only ever existed in `amount_received`. The backend already sends
// this as `payments`; the fallback keeps older cached responses correct.
function paymentsOf(s) {
  if (Array.isArray(s?.payments) && s.payments.length > 0) return s.payments;
  const txs = Array.isArray(s?.transactions) ? s.transactions : [];
  const tracked = txs.reduce((n, t) => n + Number(t?.amount || 0), 0);
  const rem = Number(s?.amount_received || 0) - tracked;
  const rows = txs.map((t) => ({ amount: Number(t?.amount || 0), date: String(t?.date || "") }));
  if (rem > 0.005) {
    rows.push({
      amount: rem,
      date: String(
        s?.legacy_payment_date ||
          (txs.length > 0 ? s?.start_date : s?.received_date || s?.start_date) ||
          ""
      ),
      legacy: true,
    });
  }
  return rows;
}


function fmtDuration(start, end) {
  return fmtMonthPeriod(start, end);
}

const kpi = (stats) => [
  { label: "Total", value: stats?.total ?? 0, color: "#0A2540", icon: Users, testId: TID.kpiTotal },
  { label: "Active", value: stats?.active ?? 0, color: "#34C759", icon: CheckCircle2, testId: TID.kpiActive },
  { label: "Expiring", value: stats?.expiring ?? 0, color: "#FF9500", icon: Clock, testId: TID.kpiExpiring },
  { label: "Expired", value: stats?.expired ?? 0, color: "#FF3B30", icon: AlertCircle, testId: TID.kpiExpired },
];

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = Math.floor(diff / 86400);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [feeReminders, setFeeReminders] = useState([]);
  const [activity, setActivity] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingCoachAttendance, setPendingCoachAttendance] = useState([]);
  const [coachesCanAdd, setCoachesCanAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [collectionMonth, setCollectionMonth] = useState(currentMonthKey());
  const [showCollection, setShowCollection] = useState(true);
  const [collectionRows, setCollectionRows] = useState(null);
  const [collectionShowAll, setCollectionShowAll] = useState(false);
  const [coachMap, setCoachMap] = useState({});
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [collectionRefreshTick, setCollectionRefreshTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, fr, cfg] = await Promise.all([
        api.stats(collectionMonth),
        api.reminders(),
        api.feeReminders(),
        api.getSettings(),
      ]);
      setStats(s);
      setReminders(r);
      setFeeReminders(fr);
      setCoachesCanAdd(cfg.coaches_can_add_students);
      if (isAdmin) {
        try {
          const [act, pend, coaches, pendingCoach] = await Promise.all([
            api.activity(30),
            api.listPendingStudents(),
            api.listCoaches(),
            api.listCoachAttendance("pending"),
          ]);
          setActivity(act);
          setPending(pend);
          const m = {};
          for (const c of coaches) m[c.id] = { name: c.full_name, phone: c.phone_number };
          setCoachMap(m);
          setPendingCoachAttendance(pendingCoach);
        } catch {
          setActivity([]);
          setPending([]);
          setCoachMap({});
          setPendingCoachAttendance([]);
        }
      }
    } finally {
      setLoading(false);
      setCollectionRefreshTick((n) => n + 1);
    }
  }, [isAdmin, collectionMonth]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-load collection rows (default expanded, latest-10)
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    api.listStudents()
      .then((all) => {
        if (!alive) return;
        // One row per payment. `payments` from the API is the union of itemised
        // transactions AND any earlier money that was only ever stored in
        // amount_received - so a student's first new transaction can no longer
        // make their earlier payments vanish from the month.
        const rows = [];
        for (const s of all) {
          for (const t of paymentsOf(s)) {
            const d = String(t?.date || "");
            const amt = Number(t?.amount || 0);
            if (d.startsWith(collectionMonth) && amt > 0) {
              rows.push({ ...s, _txDate: d, _txAmount: amt });
            }
          }
        }
        rows.sort((a, b) => (b._txDate || "").localeCompare(a._txDate || ""));
        setCollectionRows(rows);
      })
      .catch(() => alive && setCollectionRows([]));
    return () => { alive = false; };
  }, [isAdmin, collectionMonth, collectionRefreshTick]);

  const canAdd = isAdmin || coachesCanAdd;

  const todayCollectionTotal = (collectionRows || []).reduce((sum, r) => {
    const d = r._txDate || r.received_date || r.start_date;
    return d === todayKey() ? sum + Number(r._txAmount ?? r.amount_received ?? 0) : sum;
  }, 0);

  const filteredReminders = selectedCoachId
    ? reminders.filter((r) => r.coach_id === selectedCoachId)
    : reminders;
  const filteredFeeReminders = selectedCoachId
    ? feeReminders.filter((r) => r.coach_id === selectedCoachId)
    : feeReminders;

  const downloadCollectionExcel = async (e) => {
    e.stopPropagation();
    try {
      const all = await api.listStudents();
      const rows = [];
      for (const s of all) {
        for (const t of paymentsOf(s)) {
          if (Number(t.amount || 0) > 0) {
            rows.push({ date: t.date, amount: t.amount, s });
          }
        }
      }
      rows.sort((a, b) => (b.date || "").localeCompare(a.date || ""));

      const headers = [
        "Date", "Amount", "Student Name", "Age", "Phone", "Coach",
        "Start Date", "End Date", "Status", "Pending Fee",
      ];
      const escapeCsv = (v) => {
        const str = String(v ?? "");
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const lines = [headers.join(",")];
      for (const { date, amount, s } of rows) {
        lines.push(
          [
            fmtDateDMY(date),
            amount,
            s.name,
            s.age,
            s.phone_number,
            s.coach_name || "",
            fmtDateDMY(s.start_date),
            fmtDateDMY(s.end_date),
            s.status,
            s.pending_fee,
          ]
            .map(escapeCsv)
            .join(",")
        );
      }
      const csvContent = "﻿" + lines.join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `collections_${todayKey()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Failed to export");
    }
  };

  const reviewCoachAttendance = async (id, status) => {
    try {
      await api.reviewCoachAttendance(id, status);
      toast.success(`Marked ${status}`);
      setPendingCoachAttendance(await api.listCoachAttendance("pending"));
    } catch (err) {
      toast.error(err.message || "Failed to update");
    }
  };

  const downloadPendingCoachAttendanceExcel = () => {
    try {
      const headers = ["Coach", "Date", "Status"];
      const escapeCsv = (v) => {
        const str = String(v ?? "");
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const lines = [headers.join(",")];
      for (const a of pendingCoachAttendance) {
        lines.push(
          [a.coach_name, fmtDateDMY(a.date), a.status].map(escapeCsv).join(",")
        );
      }
      const csvContent = "﻿" + lines.join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pending_coach_attendance_${todayKey()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Failed to export");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-slate-500 text-sm">
            Hi, <span className="font-semibold text-slate-900">{user?.full_name?.split(" ")[0]}</span> 👋
          </p>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mt-1">Dashboard</h1>
        </div>
        {canAdd && (
          <Link
            to="/students/new"
            data-testid={TID.addStudentHeader}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0A2540] hover:bg-[#081D33] text-white font-semibold text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Student
          </Link>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {kpi(stats).map((k) => (
          <div
            key={k.label}
            data-testid={k.testId}
            className="bg-slate-50 rounded-xl p-3 border border-slate-100"
          >
            <div className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase truncate">{k.label}</div>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-0.5">
              {loading ? "—" : k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Pending Approvals — Admin only */}
      {isAdmin && (
        <section>
          <SectionHead title="Pending Approvals" sub={`${pending.length} to review`} />
          {pending.length === 0 ? (
            <EmptyState
              icon={UserPlus}
              color="#8E8E93"
              title="Nothing to approve"
              text="Students submitted by coaches will appear here."
            />
          ) : (
            <div data-testid={TID.pendingList} className="space-y-2">
              {pending.map((s) => (
                <PendingRow key={s.id} s={s} onDone={load} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Pending Coach Attendance — Admin only */}
      {isAdmin && (
        <section>
          <SectionHead title="Pending Coach Attendance" sub={`${pendingCoachAttendance.length} to review`} />
          {pendingCoachAttendance.length === 0 ? (
            <EmptyState
              icon={Clock}
              color="#8E8E93"
              title="Nothing to review"
              text="Coach attendance submissions will appear here."
            />
          ) : (
            <div data-testid={TID.pendingCoachAttendanceList} className="space-y-2">
              <div className="flex justify-end">
                <div
                  role="button"
                  tabIndex={0}
                  data-testid={TID.pendingCoachAttendanceDownload}
                  title="Download pending coach attendance"
                  onClick={downloadPendingCoachAttendanceExcel}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") downloadPendingCoachAttendanceExcel();
                  }}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  Excel
                </div>
              </div>
              {pendingCoachAttendance.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100"
                >
                  <div className="w-10 h-10 rounded-full bg-[#FF6B6B] text-white flex items-center justify-center font-bold shrink-0">
                    {a.coach_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate">{a.coach_name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {a.date}
                    </p>
                  </div>
                  <button
                    data-testid={TID.coachReviewApprove(a.id)}
                    onClick={() => reviewCoachAttendance(a.id, "approved")}
                    className="w-9 h-9 rounded-full text-[#34C759] hover:bg-[#34C75922] flex items-center justify-center shrink-0"
                    title="Approve"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={TID.coachReviewReject(a.id)}
                    onClick={() => reviewCoachAttendance(a.id, "rejected")}
                    className="w-9 h-9 rounded-full text-[#FF3B30] hover:bg-[#FF3B3022] flex items-center justify-center shrink-0"
                    title="Reject"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isAdmin && (
        <div
          data-testid="kpi-collection-month"
          className="bg-[#0A2540] text-white rounded-xl border border-[#0A2540]"
        >
          <button
            type="button"
            data-testid="collection-toggle"
            onClick={() => setShowCollection((v) => !v)}
            className="w-full text-left p-5 flex flex-wrap items-center gap-4 hover:bg-[#0e2c4a] rounded-xl transition-colors"
          >
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-white/70">Total Collection</div>
              <div className="text-3xl font-extrabold mt-0.5">
                {loading
                  ? "—"
                  : Number(stats?.collection_this_month || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="pl-4 border-l border-white/10" data-testid="kpi-collection-today">
              <div className="text-xs font-bold uppercase tracking-wider text-white/70">Today</div>
              <div className="text-xl font-bold mt-0.5">
                {collectionRows === null ? "—" : todayCollectionTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/70">
                Month
              </label>
              <input
                data-testid="collection-month-picker"
                type="month"
                value={collectionMonth}
                max={currentMonthKey()}
                onChange={(e) => {
                  setCollectionMonth(e.target.value || currentMonthKey());
                  setCollectionRows(null); // invalidate table for the new month
                  setCollectionShowAll(false);
                }}
                className="bg-white/10 border border-white/20 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:bg-white/20"
                style={{ colorScheme: "dark" }}
              />
              <div
                role="button"
                tabIndex={0}
                data-testid="collection-download-excel"
                title="Download all entries and student data"
                onClick={downloadCollectionExcel}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") downloadCollectionExcel(e);
                }}
                className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/10 hover:bg-white/20 text-xs font-semibold cursor-pointer"
              >
                <Download className="w-3.5 h-3.5" />
                Excel
              </div>
            </div>
          </button>

          {showCollection && (
            <div data-testid="collection-details" className="border-t border-white/10 bg-white/5 rounded-b-xl px-5 py-4">
              {collectionRows === null ? (
                <p className="text-sm text-white/70">Loading...</p>
              ) : collectionRows.length === 0 ? (
                <p className="text-sm text-white/70">No payments recorded this month.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white/70 text-xs uppercase tracking-wider">
                        <th className="text-left py-2 pr-4">Date</th>
                        <th className="text-right py-2 pr-4">Amount</th>
                        <th className="text-left py-2 pr-4">Student</th>
                        <th className="text-left py-2 pr-4">Coach</th>
                        <th className="text-left py-2">Module</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      {(collectionShowAll ? collectionRows : collectionRows.slice(0, 20)).map((r, i) => (
                        <tr key={r.id + "-" + i} className="border-t border-white/10">
                          <td className="py-2 pr-4 whitespace-nowrap">
                            {fmtDateDMY(r._txDate || r.received_date || r.start_date)}
                          </td>
                          <td className="py-2 pr-4 text-right font-semibold">
                            {Number(r._txAmount ?? r.amount_received ?? 0).toLocaleString("en-IN")}
                          </td>
                          <td className="py-2 pr-4">
                            <Link to={`/students/${r.id}`} className="font-semibold hover:underline">
                              {r.name}
                            </Link>
                          </td>
                          <td className="py-2 pr-4 text-white/80">{r.coach_name || "—"}</td>
                          <td className="py-2 whitespace-nowrap">
                            {fmtMonthPeriod(r.start_date, r.end_date) || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!collectionShowAll && collectionRows.length > 20 && (
                    <button
                      type="button"
                      data-testid="collection-show-more"
                      onClick={() => setCollectionShowAll(true)}
                      className="mt-3 text-sm font-semibold text-white/80 hover:text-white underline underline-offset-2"
                    >
                      Show More Entries
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Priority Reminders */}
      <section>
        <SectionHead title="Priority Reminders" sub={`${filteredReminders.length} to send`} />
        {(reminders.length > 0 || feeReminders.length > 0) && Object.keys(coachMap).length > 0 && (() => {
          const reminderCoachIds = Array.from(
            new Set(
              [...reminders, ...feeReminders].map((r) => r.coach_id).filter(Boolean)
            )
          );
          const options = reminderCoachIds
            .map((cid) => ({ id: cid, ...coachMap[cid] }))
            .filter((c) => c.name);
          if (options.length === 0) return null;
          return (
          <div className="mb-3 flex items-center gap-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Notify Coach:
            </label>
            <select
              data-testid="notify-coach-select"
              value={selectedCoachId}
              onChange={(e) => setSelectedCoachId(e.target.value)}
              className="h-9 px-3 rounded-full text-xs font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 outline-none focus:border-[#0A2540]"
            >
              <option value="">Select coach…</option>
              {options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="notify-coach-send"
              disabled={!selectedCoachId}
              onClick={() => {
                const cid = selectedCoachId;
                if (!cid) return;
                const info = coachMap[cid];
                if (!info?.phone) return;
                const renewals = reminders.filter((r) => r.coach_id === cid);
                const fees = feeReminders.filter((r) => r.coach_id === cid);
                const parts = [
                  `Hi ${info.name || "Coach"}, please follow up on the students below at Pathaans Academy Ludhiana:`,
                ];
                if (renewals.length) {
                  parts.push(
                    `\n*Renewals (${renewals.length})*\n` +
                      renewals
                        .map((r, i) => {
                          const st =
                            r.days_left < 0
                              ? `expired ${Math.abs(r.days_left)}d ago on ${r.end_date}`
                              : r.days_left === 0
                              ? `expires TODAY (${r.end_date})`
                              : `ends in ${r.days_left}d on ${r.end_date}`;
                          return `${i + 1}. ${r.name} — ${st}`;
                        })
                        .join("\n")
                  );
                }
                if (fees.length) {
                  parts.push(
                    `\n*Pending Fees (${fees.length})*\n` +
                      fees
                        .map(
                          (r, i) =>
                            `${i + 1}. ${r.name} — ₹${Number(r.pending_fee || 0).toLocaleString("en-IN")} pending${
                              r.next_due_date ? ` (due ${r.next_due_date})` : ""
                            }`
                        )
                        .join("\n")
                  );
                }
                if (!renewals.length && !fees.length) {
                  parts.push(`\nAll clear — no pending items right now.`);
                }
                parts.push(`\n— Pathaans Academy Ludhiana`);
                openWhatsApp(info.phone, parts.join("\n"));
                setSelectedCoachId("");
              }}
              className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1DA851] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center shrink-0"
              title="Send WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </div>
          );
        })()}
        {filteredReminders.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            color="#34C759"
            title="All caught up"
            text={selectedCoachId ? "No renewals due for this coach." : "No renewals due this week."}
          />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredReminders.map((s) => (
              <ReminderCard
                key={s.id}
                s={s}
                coach={coachMap[s.coach_id]}
                allReminders={reminders}
                allFeeReminders={feeReminders}
              />
            ))}
          </div>
        )}
      </section>

      {/* Fee Reminders */}
      <section>
        <SectionHead title="Fee Reminders" sub={`${filteredFeeReminders.length} pending`} />
        {filteredFeeReminders.length === 0 ? (
          <EmptyState
            icon={Wallet}
            color="#34C759"
            title="No pending fees"
            text={selectedCoachId ? "No pending fees for this coach." : "All installments are on track."}
          />
        ) : (
          <div className="space-y-3">
            {filteredFeeReminders.map((s) => (
              <FeeReminderCard key={s.id} s={s} onDone={load} />
            ))}
          </div>
        )}
      </section>

      {/* Coach Activity Tracker — Admin only */}
      {isAdmin && (
        <section data-testid={TID.activityFeed}>
          <SectionHead title="Coach Activity" sub={`${activity.length}`} />
          {activity.length === 0 ? (
            <div data-testid={TID.activityEmpty}>
              <EmptyState icon={Activity} color="#8E8E93" title="No recent activity" text="Coach actions will appear here." />
            </div>
          ) : (
            <div className="space-y-2">
              {activity.map((a) => (
                <div
                  key={a.id}
                  data-testid={TID.activityRow(a.id)}
                  className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border border-slate-100"
                >
                  <div className="w-9 h-9 rounded-full bg-[#FF6B6B] text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {a.actor_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900 font-semibold leading-snug">
                      {a.actor_name} {a.action}
                      {a.target_name ? ` ${a.target_name}` : ""}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{timeAgo(a.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function SectionHead({ title, sub }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      <span className="text-xs text-slate-500 font-medium">{sub}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, color, title, text }) {
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 py-8 flex flex-col items-center text-center">
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
        style={{ background: color + "22" }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <p className="font-bold text-slate-900">{title}</p>
      <p className="text-sm text-slate-500 mt-0.5">{text}</p>
    </div>
  );
}

function ReminderCard({ s, coach, allReminders = [], allFeeReminders = [] }) {
  const isExpired = s.days_left < 0;
  const color = isExpired || s.days_left <= 1 ? "#FF3B30" : s.days_left <= 3 ? "#FF9500" : "#0A2540";
  const text = isExpired
    ? `Expired ${Math.abs(s.days_left)}d ago`
    : s.days_left === 0
    ? "Expires today"
    : `${s.days_left}d left`;
  const coachPhone = coach?.phone;

  const notifyCoachAll = () => {
    const cid = s.coach_id;
    const renewals = allReminders.filter((r) => r.coach_id === cid);
    const fees = allFeeReminders.filter((r) => r.coach_id === cid);
    const renewalLines = renewals.map((r, i) => {
      const st =
        r.days_left < 0
          ? `expired ${Math.abs(r.days_left)}d ago on ${r.end_date}`
          : r.days_left === 0
          ? `expires TODAY (${r.end_date})`
          : `ends in ${r.days_left}d on ${r.end_date}`;
      return `${i + 1}. ${r.name} — ${st}`;
    });
    const feeLines = fees.map((r, i) => {
      const amt = Number(r.pending_fee || 0).toLocaleString("en-IN");
      return `${i + 1}. ${r.name} — ₹${amt} pending${r.next_due_date ? ` (due ${r.next_due_date})` : ""}`;
    });
    const parts = [
      `Hi ${s.coach_name || "Coach"}, please follow up on the students below at Pathaans Academy Ludhiana:`,
    ];
    if (renewalLines.length) parts.push(`\n*Renewals (${renewalLines.length})*\n${renewalLines.join("\n")}`);
    if (feeLines.length) parts.push(`\n*Pending Fees (${feeLines.length})*\n${feeLines.join("\n")}`);
    if (!renewalLines.length && !feeLines.length) parts.push(`\nAll clear — no pending items right now.`);
    parts.push(`\n— Pathaans Academy Ludhiana`);
    openWhatsApp(coachPhone, parts.join("\n"));
  };

  return (
    <Link
      to={`/students/${s.id}`}
      data-testid={TID.reminderCard(s.id)}
      className="flex items-center gap-3 bg-slate-50 hover:bg-slate-100 rounded-xl p-3 border border-slate-100 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-900 text-sm truncate">{s.name}</p>
        <p className="text-xs text-slate-600 truncate">
          {s.coach_name || "—"}
          {fmtMonthPeriod(s.start_date, s.end_date) && (
            <span className="text-slate-400"> | {fmtMonthPeriod(s.start_date, s.end_date)}</span>
          )}
        </p>
        <span
          className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
          style={{ background: color + "22", color }}
        >
          {text}
        </span>
      </div>
      <button
        data-testid={TID.reminderWhatsApp(s.id)}
        onClick={(e) => {
          e.preventDefault();
          openWhatsApp(s.phone_number, buildReminderMessage(s));
        }}
        className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center shrink-0"
        title="Send WhatsApp reminder"
      >
        <MessageCircle className="w-4 h-4" />
      </button>
    </Link>
  );
}

function FeeReminderCard({ s, onDone }) {
  const today = new Date();
  const due = s.next_due_date ? new Date(s.next_due_date) : today;
  const days = Math.floor((due - today) / (1000 * 60 * 60 * 24));
  const isOverdue = days < 0;
  const color = isOverdue || days <= 1 ? "#FF3B30" : days <= 3 ? "#FF9500" : "#0A2540";
  const text = isOverdue ? `Overdue ${Math.abs(days)}d` : days === 0 ? "Due today" : `Due in ${days}d`;

  const [paidInput, setPaidInput] = useState("");
  const [saving, setSaving] = useState(false);

  const savePayment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const amt = Number(paidInput);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      const newPending = Math.max(0, Number(s.pending_fee || 0) - amt);
      const newReceived = Number(s.amount_received || 0) + amt;
      const today = new Date().toISOString().slice(0, 10);
      await api.updateStudent(s.id, {
        pending_fee: newPending,
        amount_received: newReceived,
        received_date: today,
        transaction_add: { amount: amt, date: today },
      });
      toast.success(`Recorded ₹${amt.toLocaleString("en-IN")} for ${s.name}`);
      setPaidInput("");
      onDone?.();
    } catch (err) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={TID.feeReminderCard(s.id)}
      className="rounded-xl p-4 border transition-colors"
      style={{ background: "#FF950010", borderColor: "#FF950055" }}
    >
      <div className="flex items-center gap-3">
        <Link
          to={`/students/${s.id}`}
          className="w-11 h-11 rounded-full bg-[#FF9500] text-white flex items-center justify-center shrink-0"
        >
          <Wallet className="w-5 h-5" />
        </Link>
        <Link to={`/students/${s.id}`} className="flex-1 min-w-0">
          <p className="font-bold text-slate-900 truncate">{s.name}</p>
          <p className="text-sm font-bold text-[#FF3B30]">
            ₹{Number(s.pending_fee || 0).toLocaleString("en-IN")} pending
          </p>
          {s.coach_name && (
            <p className="text-xs font-semibold text-[#FF6B6B] mt-0.5 truncate">Coach: {s.coach_name}</p>
          )}
          <span
            className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ background: color + "22", color }}
          >
            {text} · {s.next_due_date}
          </span>
        </Link>
        <button
          data-testid={TID.feeReminderWhatsApp(s.id)}
          onClick={() => openWhatsApp(s.phone_number, buildFeeReminderMessage(s))}
          className="w-11 h-11 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center shrink-0"
          title="Send WhatsApp reminder"
        >
          <MessageCircle className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={savePayment} className="mt-3 flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-lg px-3 h-10 bg-white focus-within:border-[#0A2540]">
          <span className="text-slate-500 text-sm font-semibold">₹</span>
          <input
            data-testid={`fee-paid-input-${s.id}`}
            type="number"
            min={0}
            step="0.01"
            value={paidInput}
            onChange={(e) => setPaidInput(e.target.value)}
            placeholder="Amount paid"
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
          />
        </div>
        <button
          type="submit"
          data-testid={`fee-save-btn-${s.id}`}
          disabled={saving || !paidInput}
          className="h-10 px-3 rounded-lg bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-50 text-white flex items-center gap-1.5 text-sm font-semibold"
          title="Save payment"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </form>
    </div>
  );
}

function PendingRow({ s, onDone }) {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await api.approveStudent(s.id);
      toast.success(`Approved ${s.name}`);
      onDone?.();
    } catch (e) {
      toast.error(e.message || "Failed to approve");
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await api.rejectStudent(s.id);
      toast.success(`Rejected ${s.name}`);
      onDone?.();
    } catch (e) {
      toast.error(e.message || "Failed to reject");
    } finally {
      setBusy(false);
    }
  };
  const durationLabel = fmtDuration(s.start_date, s.end_date);
  return (
    <div className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
      <div className="w-11 h-11 rounded-full bg-[#FF9500] text-white flex items-center justify-center font-bold shrink-0 mt-0.5">
        {s.name?.charAt(0)?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-900 break-words">{s.name}</p>
        <p className="text-xs text-slate-500 truncate">
          Age {s.age} · {s.phone_number}
        </p>
        {s.coach_name && (
          <p className="text-xs font-semibold text-[#FF6B6B] mt-0.5 truncate">
            {s.coach_name}
          </p>
        )}
        <p className="text-xs text-slate-700 mt-1 font-semibold">
          {fmtDate(s.start_date)} - {fmtDate(s.end_date)}
          {durationLabel && (
            <span className="text-slate-500 font-medium"> ({durationLabel})</span>
          )}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <Link
          to={`/students/${s.id}/edit`}
          data-testid={TID.pendingReview(s.id)}
          className="h-9 px-3 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1 text-xs font-semibold"
          title="Review & edit"
        >
          Review
        </Link>
        <div className="flex gap-2">
          <button
            data-testid={TID.pendingApprove(s.id)}
            onClick={approve}
            disabled={busy}
            className="w-9 h-9 rounded-full bg-[#34C759] hover:bg-[#2AA84A] disabled:opacity-50 text-white flex items-center justify-center"
            title="Approve"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            data-testid={TID.pendingReject(s.id)}
            onClick={reject}
            disabled={busy}
            className="w-9 h-9 rounded-full bg-[#FF3B30] hover:bg-[#D93028] disabled:opacity-50 text-white flex items-center justify-center"
            title="Reject"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

